import { toPositiveOptionId } from '@/utils/optionValues';
import { buildFirstInstallmentOptions as buildSharedFirstInstallmentOptions } from '@/composables/shared/useFirstInstallmentOptions';
import {
  EXPENSE_FEE_STATUS_OPTIONS,
  EXPENSE_FEE_FREQUENCY_OPTIONS,
  EXPENSE_FEE_TAX_MODE_OPTIONS,
  EXPENSE_FEE_LOCAL_TAX_TYPE_OPTIONS,
  parseExpenseFeeNumericInput,
  formatExpenseFeeDateForApi,
  buildTodayDate,
  createInitialExpenseFeeTaxPreview,
  normalizeExpenseFeeLocalTaxes,
  buildExpenseFeeTagsPayload,
  buildExpenseFeeTaxPreviewPayload,
  buildExpenseFeeStoreRequestData,
} from '@/composables/expensesFees/shared/useExpenseFeeModalFormShared';
import { FEES_TEXT } from '@/texts/catalog/fees';

export const FEE_MODAL_SECTIONS = [
  {
    key: 'general',
    icon: 'fa-file-text-o',
  },
  {
    key: 'operational',
    icon: 'fa-cogs',
  },
  {
    key: 'details',
    icon: 'fa-align-left',
  },
  {
    key: 'files',
    icon: 'fa-paperclip',
  },
];

export const FEE_COMMISSION_TYPE_OPTIONS = [
  { value: 'collaborator', label: FEES_TEXT.modal.selectOptions.commissionTypes.collaborator },
  { value: 'supplier', label: FEES_TEXT.modal.selectOptions.commissionTypes.supplier },
];

export const FEE_DOCUMENT_TYPE_OPTIONS = [
  { value: 'cfdi', label: FEES_TEXT.modal.selectOptions.documentTypes.cfdi },
  { value: 'simple_note', label: FEES_TEXT.modal.selectOptions.documentTypes.simpleNote },
];

export const FEE_LOCAL_TAX_TYPE_OPTIONS = [
  ...EXPENSE_FEE_LOCAL_TAX_TYPE_OPTIONS,
];

export { buildSharedFirstInstallmentOptions as buildFirstInstallmentOptions };

export const createInitialFeeForm = () => ({
  commissionType: 'collaborator',
  voucherType: 'cfdi',
  expenseStatus: 'pending',
  currencyCode: 'MXN',
  exchangeRate: 1,
  amount: 0,
  discount: 0,
  deductions: 0,
  withoutTaxes: false,
  taxType: 'individual',
  taxes: [],
  taxGroup: null,
  localTaxes: [],
  supplier: null,
  collaborator: null,
  supplierContributor: null,
  expenseClass: null,
  expenseCategory: null,
  frequency: 'unique',
  expensesCount: 1,
  firstInstallmentOption: null,
  periodicityOption: null,
  folio: '',
  concept: FEES_TEXT.modal.defaultConcept,
  expenseDate: buildTodayDate(),
  paymentDate: buildTodayDate(),
  cfdiDate: buildTodayDate(),
  customer: null,
  sale: null,
  invoicePdfFile: null,
  invoiceXmlFile: null,
  voucherFile: null,
  purchaseOrderFile: null,
  additionalFile: null,
  additionalFileMode: 'file',
  additionalFileUrl: '',
  tags: [],
  area: null,
  comments: '',
});

export const createInitialFeeTaxPreview = () => ({
  ...createInitialExpenseFeeTaxPreview(),
});

export const createInitialFeeModalOptions = () => ({
  commissionTypes: FEE_COMMISSION_TYPE_OPTIONS,
  documentTypes: FEE_DOCUMENT_TYPE_OPTIONS,
  expenseStatuses: EXPENSE_FEE_STATUS_OPTIONS,
  frequencies: EXPENSE_FEE_FREQUENCY_OPTIONS,
  taxModes: EXPENSE_FEE_TAX_MODE_OPTIONS,
  localTaxTypes: FEE_LOCAL_TAX_TYPE_OPTIONS,
  currencies: [],
  suppliers: [],
  collaborators: [],
  supplierContributors: [],
  customers: [],
  sales: [],
  taxes: [],
  taxGroups: [],
  periodicities: [],
  tags: [],
  areas: [],
});

export const buildFeeTaxPreviewPayload = (form = {}) => {
  return buildExpenseFeeTaxPreviewPayload(form);
};

export const buildFeePayload = (form = {}, taxPreview = null) => {
  const payloadPreview = taxPreview && typeof taxPreview === 'object'
    ? taxPreview
    : createInitialFeeTaxPreview();

  const { tagIds, tagNames } = buildExpenseFeeTagsPayload(form?.tags);
  const discount = parseExpenseFeeNumericInput(form?.discount);
  const deductions = parseExpenseFeeNumericInput(form?.deductions);
  const withoutTaxes = Boolean(form?.withoutTaxes);
  const taxType = String(form?.taxType || 'individual').trim().toLowerCase();
  const commissionType = String(form?.commissionType || 'collaborator').trim().toLowerCase();
  const isCollaboratorCommission = commissionType === 'collaborator';

  return {
    commission_type: commissionType,
    voucher_type: String(form?.voucherType || 'cfdi').trim().toLowerCase(),
    expense_status: 'pending',
    currency: String(form?.currencyCode || 'MXN').trim().toUpperCase(),
    exchange_rate: Number(form?.exchangeRate || 0) > 0 ? Number(form.exchangeRate) : 1,
    amount: parseExpenseFeeNumericInput(form?.amount),
    // Keep legacy discount behavior by sending combined discount amount.
    discount: discount + deductions,
    deductions,
    tax_type: withoutTaxes ? 'individual' : taxType,
    tax_ids: withoutTaxes
      ? []
      : (Array.isArray(form?.taxes) ? form.taxes : [])
        .map((item) => toPositiveOptionId(item))
        .filter((id) => id > 0),
    tax_group_id: withoutTaxes ? 0 : toPositiveOptionId(form?.taxGroup),
    local_taxes: normalizeExpenseFeeLocalTaxes(form?.localTaxes),
    supplier_id: isCollaboratorCommission ? 0 : toPositiveOptionId(form?.supplier),
    collaborator_id: isCollaboratorCommission ? toPositiveOptionId(form?.collaborator) : 0,
    class_id: toPositiveOptionId(form?.expenseClass),
    category_id: toPositiveOptionId(form?.expenseCategory),
    frequency: String(form?.frequency || 'unique').trim().toLowerCase(),
    expenses_count: Math.max(1, Number(form?.expensesCount || 1) || 1),
    first_installment: String(form?.firstInstallmentOption?.value || '').trim(),
    periodicity_code: String(form?.periodicityOption?.value || '').trim().toLowerCase(),
    folio: String(form?.folio || '').trim(),
    concept: String(form?.concept || '').trim(),
    date: formatExpenseFeeDateForApi(form?.expenseDate),
    payment_date: formatExpenseFeeDateForApi(form?.paymentDate),
    cfdi_date: formatExpenseFeeDateForApi(form?.cfdiDate),
    customer_id: toPositiveOptionId(form?.customer),
    sale_id: toPositiveOptionId(form?.sale),
    area_id: toPositiveOptionId(form?.area),
    tag_ids: tagIds,
    tags: tagNames,
    comments: String(form?.comments || '').trim(),
    invoice_pdf_file: form?.invoicePdfFile || null,
    invoice_xml_file: form?.invoiceXmlFile || null,
    voucher_file: form?.voucherFile || null,
    purchase_order_file: form?.purchaseOrderFile || null,
    additional_file: form?.additionalFile || null,
    additional_file_url: String(form?.additionalFileUrl || '').trim(),
    tax_preview: payloadPreview,
  };
};

export const buildFeeStoreRequestData = (payload = {}) => {
  return buildExpenseFeeStoreRequestData(payload);
};
